import _defineProperty from "/drone/src/node_modules/@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "/drone/src/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "/drone/src/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["disabled", "isActionSeparatorDisplayed", "error", "label", "onSelect", "onRenderSelectedValue", "options", "onChange", "multiple", "formatOption", "enableSelectAllOption", "selectAllLabel", "enableSearchOption", "enableOptionContentEllipsis", "enableFooter", "preserveSelection", "onCancelClick", "onConfirmClick", "footerContent", "searchPlaceholder", "defaultSearchQuery", "tagLabelMaxLength", "enableVirtualization", "openTagsCount", "renderAggregatedTagsLabel", "singleLine", "zIndex", "enablePortal", "groups", "data-testid-input", "open", "defaultOpen", "onOpenChange", "isListExpanded", "autoFocus", "defaultValue", "name", "placeholder", "value", "tabIndex"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import { useCallback, useEffect, useRef, useState } from 'react';
import { useDetectEscapeKeyPressed, useOnValueUpdate, useOptions, useSelectedValueToDisplay, useValue } from '../../../utils';
import { useControlledState, useDerivedState, useOuterClick, useOverlay, usePopper, useRootRef } from '../../../system';
import { useOnOptionListUpdate } from './useOnOptionListUpdate';
import { usePanelWidth } from './usePanelWidth';
import { MAX_OPTIONS_SELECT_ALL_THRESHOLD } from '../../OptionList/constants';
export var useDropdown = function useDropdown(_ref, ref) {
  var disabled = _ref.disabled,
      _ref$isActionSeparato = _ref.isActionSeparatorDisplayed,
      isActionSeparatorDisplayed = _ref$isActionSeparato === void 0 ? true : _ref$isActionSeparato,
      error = _ref.error,
      label = _ref.label,
      onSelect = _ref.onSelect,
      onRenderSelectedValue = _ref.onRenderSelectedValue,
      options = _ref.options,
      onChange = _ref.onChange,
      _ref$multiple = _ref.multiple,
      multiple = _ref$multiple === void 0 ? false : _ref$multiple,
      formatOption = _ref.formatOption,
      enableSelectAllOption = _ref.enableSelectAllOption,
      selectAllLabel = _ref.selectAllLabel,
      enableSearchOption = _ref.enableSearchOption,
      enableOptionContentEllipsis = _ref.enableOptionContentEllipsis,
      enableFooter = _ref.enableFooter,
      preserveSelection = _ref.preserveSelection,
      onCancelClick = _ref.onCancelClick,
      onConfirmClick = _ref.onConfirmClick,
      footerContent = _ref.footerContent,
      searchPlaceholder = _ref.searchPlaceholder,
      defaultSearchQuery = _ref.defaultSearchQuery,
      tagLabelMaxLength = _ref.tagLabelMaxLength,
      enableVirtualization = _ref.enableVirtualization,
      _ref$openTagsCount = _ref.openTagsCount,
      openTagsCount = _ref$openTagsCount === void 0 ? enableVirtualization ? 20 : 10 : _ref$openTagsCount,
      renderAggregatedTagsLabel = _ref.renderAggregatedTagsLabel,
      singleLine = _ref.singleLine,
      zIndex = _ref.zIndex,
      enablePortal = _ref.enablePortal,
      groups = _ref.groups,
      dataTestIdInput = _ref['data-testid-input'],
      open = _ref.open,
      defaultOpen = _ref.defaultOpen,
      onOpenChange = _ref.onOpenChange,
      isListExpanded = _ref.isListExpanded,
      autoFocus = _ref.autoFocus,
      defaultValue = _ref.defaultValue,
      name = _ref.name,
      placeholder = _ref.placeholder,
      value = _ref.value,
      tabIndex = _ref.tabIndex,
      restProps = _objectWithoutProperties(_ref, _excluded);

  var internalInputRef = useRootRef(ref);
  var containerRef = useRef(null);
  var dropdownRef = useRef(null);

  var _useState = useState([]),
      _useState2 = _slicedToArray(_useState, 2),
      initialSelection = _useState2[0],
      setInitialSelection = _useState2[1];

  var _useValue = useValue(value, defaultValue),
      _useValue2 = _slicedToArray(_useValue, 2),
      internalValue = _useValue2[0],
      setInternalValue = _useValue2[1];

  var _useState3 = useState(defaultSearchQuery),
      _useState4 = _slicedToArray(_useState3, 2),
      searchQuery = _useState4[0],
      setSearchQuery = _useState4[1];

  var _useOptions = useOptions(options, formatOption),
      internalOptions = _useOptions.internalOptions,
      isFlat = _useOptions.isFlat;

  var _useDerivedState = useDerivedState(function () {
    return internalOptions;
  }, [internalOptions]),
      _useDerivedState2 = _slicedToArray(_useDerivedState, 2),
      dynamicInternalOptions = _useDerivedState2[0],
      setDynamicInternalOptions = _useDerivedState2[1];

  var _useControlledState = useControlledState(isListExpanded !== undefined ? isListExpanded : defaultOpen, open, onOpenChange),
      _useControlledState2 = _slicedToArray(_useControlledState, 2),
      isOptionListShown = _useControlledState2[0],
      setIsOptionListShown = _useControlledState2[1];

  useEffect(function () {
    if (enableFooter) {
      setInitialSelection(internalValue);
    }
  }, [enableFooter]);
  useOnOptionListUpdate(internalOptions, dynamicInternalOptions, setInternalValue, searchQuery, isOptionListShown, preserveSelection);
  var onValueUpdate = useOnValueUpdate(setInternalValue, onSelect, onChange, name, initialSelection);
  var selectedValueToDisplay = useSelectedValueToDisplay(internalValue, internalOptions, onRenderSelectedValue);
  var handleCancelClick = useCallback(function () {
    setInternalValue(initialSelection);

    if (enableFooter && onCancelClick) {
      onCancelClick(initialSelection);
    }
  }, [enableFooter, onCancelClick, initialSelection]);
  var handleConfirmClick = useCallback(function () {
    setInitialSelection(internalValue);

    if (enableFooter && onConfirmClick) {
      onConfirmClick(internalValue);
    }
  }, [enableFooter, onConfirmClick, internalValue]);
  var blurInput = useCallback(function () {
    var node = internalInputRef.current;

    if (node && node.blur) {
      setTimeout(function () {
        return node.blur();
      }, 0);
    }
  }, [internalInputRef]);

  var focusOnActionItemTrigger = function focusOnActionItemTrigger() {
    var node = internalInputRef.current;

    if (!isOptionListShown) {
      if (node && node.focus) {
        node.focus();
      }
    } else {
      blurInput();
    }
  };

  var onOuterClick = useCallback(function () {
    setIsOptionListShown(false);
  }, [setIsOptionListShown]);

  if (!enableFooter) {
    useOuterClick([containerRef, dropdownRef], onOuterClick, isOptionListShown);
  }

  useDetectEscapeKeyPressed(containerRef, setIsOptionListShown, isOptionListShown && !enableFooter);
  var onOptionListVisibilityTriggerClick = useCallback(function () {
    if (disabled) {
      return;
    }

    setIsOptionListShown(!isOptionListShown);
    focusOnActionItemTrigger();
  }, [setIsOptionListShown, isOptionListShown, disabled]);

  var _usePopper = usePopper(dropdownRef, containerRef, {
    align: 'bottom-start',
    offset: [0, 5],
    allowedAutoPlacements: ['top', 'bottom']
  }),
      panelProps = _usePopper.panelProps,
      arrowProps = _usePopper.arrowProps;

  var _useOverlay = useOverlay(isOptionListShown, {
    zIndex: zIndex
  }),
      realZIndex = _useOverlay.zIndex;

  usePanelWidth(containerRef, dropdownRef);
  return {
    rootProps: restProps,
    containerProps: {
      ref: containerRef
    },
    textInputProps: {
      onClick: onOptionListVisibilityTriggerClick,
      ref: internalInputRef,
      value: selectedValueToDisplay,
      internalValue: internalValue,
      disabled: disabled,
      error: error,
      label: label,
      name: name,
      displayMode: 'block',
      isCustomRenderer: !!onRenderSelectedValue,
      'data-testid-input': dataTestIdInput,
      autoComplete: 'off',
      readOnly: true,
      autoFocus: autoFocus,
      placeholder: placeholder,
      tabIndex: tabIndex,
      multiple: multiple
    },
    portalProps: {
      enablePortal: enablePortal
    },
    listContainerProps: _objectSpread({
      ref: dropdownRef,
      zIndex: realZIndex
    }, panelProps),
    optionListProps: {
      disabled: disabled,
      options: options,
      multiple: multiple,
      internalValue: internalValue,
      blurInput: blurInput,
      isOptionListShown: isOptionListShown,
      setInternalValue: setInternalValue,
      setIsOptionListShown: setIsOptionListShown,
      onValueUpdate: onValueUpdate,
      onChange: onChange,
      isFlat: isFlat,
      internalOptions: internalOptions,
      dynamicInternalOptions: dynamicInternalOptions,
      enableVirtualization: enableVirtualization,
      setInternalOptions: setDynamicInternalOptions,
      setSearch: setSearchQuery,
      enableSelectAllOption: options && options.length > MAX_OPTIONS_SELECT_ALL_THRESHOLD ? false : enableSelectAllOption,
      selectAllLabel: selectAllLabel,
      enableSearchOption: enableSearchOption,
      enableFooter: enableFooter,
      onCancelClick: handleCancelClick,
      onConfirmClick: handleConfirmClick,
      footerContent: footerContent,
      searchPlaceholder: searchPlaceholder,
      defaultSearchQuery: defaultSearchQuery,
      enableAbsolutePositioning: false,
      groups: groups,
      enableOptionContentEllipsis: enableOptionContentEllipsis
    },
    arrowProps: arrowProps,
    dropdownExpansionIndicatorProps: {
      disabled: disabled,
      error: error,
      multiple: multiple,
      displaySeparator: !multiple && isActionSeparatorDisplayed && (isOptionListShown || !!internalValue.length),
      isExpanded: isOptionListShown,
      onClick: onOptionListVisibilityTriggerClick
    },
    dropdownBeforeSectionProps: {
      disabled: disabled,
      onClick: onOptionListVisibilityTriggerClick,
      internalValue: internalValue,
      internalOptions: internalOptions,
      multiple: multiple,
      onRenderSelectedValue: onRenderSelectedValue,
      setInternalValue: setInternalValue,
      setInitialSelection: setInitialSelection,
      onSelect: onSelect,
      onChange: onChange,
      options: options,
      isFlat: isFlat,
      tagLabelMaxLength: tagLabelMaxLength,
      openTagsCount: openTagsCount,
      renderAggregatedTagsLabel: renderAggregatedTagsLabel,
      singleLine: singleLine
    },
    disabled: disabled,
    multiple: multiple,
    onRenderSelectedValue: onRenderSelectedValue,
    internalValue: internalValue,
    open: isOptionListShown
  };
};